import { cn } from '../../../../../../lib/utils';
import type { BorkConnection } from '../../../../../../services/admin/adminService';
import type { TFn } from '../../types';

/**
 * The pieces of the Bork connection UI that both of its homes render identically — the AI Checker
 * panel (operational: dry run / backfill / override) and the Admin Setup page (configuration, next
 * to the revenue sources the connection feeds).
 */

/** Same short local format the revenue-automation admin page uses for run timestamps. */
export const formatRunAt = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

/**
 * Outcome of the connection's last automatic pull. Green/red/grey; the failure message rides in the
 * title attribute because it is often a whole stack of gateway text.
 */
export function BorkLastRunChip({ connection, t }: { connection: BorkConnection; t: TFn }) {
  const { lastRunStatus, lastRunAt, lastRunError } = connection;
  return (
    <span
      className={cn('px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap shrink-0',
        lastRunStatus === 'success'
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
          : lastRunStatus === 'failure'
          ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300'
          : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300')}
      title={lastRunError ?? (lastRunAt ? formatRunAt(lastRunAt) : t('adminTesting.bork.lastRunNeverTitle'))}
    >
      {lastRunStatus === 'success'
        ? t('adminTesting.bork.lastRunSuccess', { at: formatRunAt(lastRunAt) })
        : lastRunStatus === 'failure'
        ? t('adminTesting.bork.lastRunFailure', { at: formatRunAt(lastRunAt) })
        : t('adminTesting.bork.lastRunNever')}
    </span>
  );
}
